import api from './api';

export interface MeetingText {
  id: number;
  name?: string | null;
  title: string;
  content: string;
  text_type: 'transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other';
  is_ai_generated: boolean;
  confidence_score: number | null;
  word_count: number | null;
  version: number;
  is_current_version: boolean;
  order: number;
  meeting_id: number;
  meeting: {
    id: number;
    title: string;
    scheduled_at: string;
  };
  created_by: {
    id: number;
    full_name: string;
    email: string;
  };
  updated_by: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface MeetingTextCreate {
  name?: string;
  title: string;
  content: string;
  text_type: 'transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other';
  is_ai_generated?: boolean;
  confidence_score?: number;
  order?: number;
  meeting_id: number;
}

export interface MeetingTextUpdate {
  name?: string;
  title?: string;
  content?: string;
  text_type?: 'transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other';
  confidence_score?: number;
}

export interface TranscriptionJobResponse {
  job_id: number;
  job_status: string;
  message: string;
  meeting_id: number;
}

export const meetingTextService = {
  async getMeetingTexts(
    meetingId: number,
    textType?: 'transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other',
    currentVersionOnly: boolean = true
  ): Promise<MeetingText[]> {
    const params = new URLSearchParams({ meeting_id: meetingId.toString() });
    if (textType) params.append('text_type', textType);
    if (currentVersionOnly) params.append('current_version_only', 'true');

    const response = await api.get(`/meeting-texts/?${params}`);
    return response.data;
  },

  async getMeetingText(textId: number): Promise<MeetingText> {
    const response = await api.get(`/meeting-texts/${textId}`);
    return response.data;
  },

  async createMeetingText(textData: MeetingTextCreate): Promise<MeetingText> {
    const response = await api.post('/meeting-texts/', textData);
    return response.data;
  },

  async updateMeetingText(textId: number, textData: MeetingTextUpdate): Promise<MeetingText> {
    const response = await api.put(`/meeting-texts/${textId}`, textData);
    return response.data;
  },

  async deleteMeetingText(textId: number): Promise<void> {
    await api.delete(`/meeting-texts/${textId}`);
  },

  async uploadTranscript(meetingId: number, file: File): Promise<MeetingText> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/meeting-texts/upload-transcript/${meetingId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async uploadRecording(meetingId: number, file: File, language?: string): Promise<TranscriptionJobResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (language) {
      formData.append('language', language);
    }

    const response = await api.post(`/meeting-texts/upload-recording/${meetingId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};